/**
 * WelcomePathModal — Type A. First login only.
 *
 * Names the three jobs the product does and records the lane the user picked,
 * which reorders the setup dock and biases later coach marks.
 */

import React, { useEffect, useState } from 'react';
import { X, Wand2, Megaphone, CalendarClock } from 'lucide-react';
import { WELCOME_COPY, WELCOME_ID, type OverlayLane } from './overlayCopy';
import { isSeen, markSeen, setLane } from './useOnboardingState';
import { useOverlaySlot } from './OverlayController';

const LANE_ICON: Record<OverlayLane, React.ElementType> = {
  studio: Wand2,
  campaign: Megaphone,
  calendar: CalendarClock,
};

const LANE_ICON_BG: Record<OverlayLane, string> = {
  studio: 'bg-primary',
  campaign: 'bg-[#0F2C63]',
  calendar: 'bg-[#0F9D6B]',
};

interface Props {
  /** True when the account has no campaigns and no creatives. */
  isNewUser: boolean;
  onNavigate: (route: string) => void;
}

export const WelcomePathModal: React.FC<Props> = ({
  isNewUser,
  onNavigate,
}) => {
  const [dismissed, setDismissed] = useState(false);
  const [alreadySeen] = useState(() => isSeen(WELCOME_ID));
  const show = useOverlaySlot(
    WELCOME_ID,
    'welcome',
    isNewUser && !dismissed && !alreadySeen
  );
  const [picked, setPicked] = useState<OverlayLane>('studio');

  const close = () => {
    markSeen(WELCOME_ID);
    setDismissed(true);
  };

  useEffect(() => {
    if (!show) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  const chosen = WELCOME_COPY.lanes.find((l) => l.lane === picked)!;

  const start = () => {
    setLane(picked);
    setDismissed(true);
    onNavigate(chosen.route);
  };

  return (
    <div
      className="fixed inset-0 z-[1050] flex items-start justify-center overflow-y-auto bg-navy/55 p-4 pt-16 sm:pt-24"
      onClick={close}
      role="presentation"
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-card dark:bg-[#1A1D26] p-6 shadow-2xl animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={WELCOME_COPY.title}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 flex-none rounded-xl bg-gradient-to-br from-primary to-[#e93164]" />
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight text-ink">
              {WELCOME_COPY.title}
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              {WELCOME_COPY.subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="ml-auto rounded p-1 text-ink-3 hover:bg-inset hover:text-ink-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {WELCOME_COPY.lanes.map((opt) => {
            const Icon = LANE_ICON[opt.lane];
            const active = picked === opt.lane;
            return (
              <button
                key={opt.lane}
                type="button"
                onClick={() => setPicked(opt.lane)}
                aria-pressed={active}
                className={[
                  'rounded-xl border p-4 text-left transition-colors',
                  active
                    ? 'border-2 border-primary bg-primary/5'
                    : 'border-line bg-card hover:border-line-2 dark:border-[#2A2F3D] dark:bg-[#232734]',
                ].join(' ')}
              >
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-lg ${LANE_ICON_BG[opt.lane]}`}
                >
                  <Icon className="h-4 w-4 text-white" />
                </div>
                <div className="mt-2.5 text-sm font-bold text-ink">
                  {opt.title}
                </div>
                <p className="mt-1 text-xs leading-relaxed text-ink-3">
                  {opt.body}
                </p>
                <div
                  className={`mt-2.5 text-[11px] font-bold tracking-wider ${
                    active ? 'text-primary' : 'text-ink-3'
                  }`}
                >
                  {opt.meta}
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={start}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primaryHover"
          >
            Start with {chosen.title.split(' ').slice(-2).join(' ')}
          </button>
          <button
            type="button"
            onClick={close}
            className="text-sm text-ink-3 hover:text-ink-2"
          >
            {WELCOME_COPY.skip}
          </button>
          <span className="ml-auto text-xs text-ink-3">
            {WELCOME_COPY.footnote}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WelcomePathModal;
